import { tokens } from "../../theme";
import { useState, useEffect } from "react";
import axios from "axios";

import {
  Box,
  Button,
  Typography,
  TextField,
  useTheme,
  IconButton,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Paper,
  Card,
  CardContent,
  Stack,
  InputAdornment,
  Pagination,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Checkbox,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import BreadcrumbsBar from "../../components/BreadcrumbsBar";
import { Engineering, PeopleSharp, Warning } from "@mui/icons-material";
import { FaBuildingUser, FaHouseUser } from "react-icons/fa6";
import SearchIcon from "@mui/icons-material/Search";
import UserCard from "../../components/UserCard"; // New component

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const iconMap = {
  "Total Users": <PeopleSharp fontSize="large" color="primary" />,
  Tenants: <FaHouseUser fontSize="large" color="success" />,
  Landlords: <FaBuildingUser fontSize="large" color="error" />,
  "Service Providers": <Engineering fontSize="large" color="info" />,
};
const StatsCard = ({ label, value }) => {
  return (
    <Card elevation={1} sx={{ p: 2, borderRadius: 3, height: "100%" }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          {iconMap[label]}
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="h5">{value}</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const renderFilterGroup = (
  label,
  options,
  selectedValues,
  setSelectedValues
) => {
  const allSelected =
    options.length > 0 && selectedValues.length === options.length;
  const partiallySelected = selectedValues.length > 0 && !allSelected;

  const toggleAll = () => {
    setSelectedValues(allSelected ? [] : [...options]);
  };

  const toggleOne = (option) => {
    setSelectedValues((prev) =>
      prev.includes(option)
        ? prev.filter((item) => item !== option)
        : [...prev, option]
    );
  };

  return (
    <Box mt={2}>
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={allSelected}
              indeterminate={partiallySelected}
              onChange={toggleAll}
            />
          }
          label={label}
        />
        <Box sx={{ display: "flex", flexDirection: "column", ml: 3 }}>
          {options.map((option) => (
            <FormControlLabel
              key={option}
              control={
                <Checkbox
                  checked={selectedValues.includes(option)}
                  onChange={() => toggleOne(option)}
                />
              }
              label={option}
            />
          ))}
        </Box>
      </FormGroup>
    </Box>
  );
};

const UserPage = () => {
  const theme = useTheme();
  // const colors = tokens(theme.palette.mode);
  const [stats, setStats] = useState({});
  const [chartData, setChartData] = useState([]);
  const [users, setUsers] = useState([]);
  const [flaggedUsers, setFlaggedUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [loading, setLoading] = useState(false);
  const [loadingUserId, setLoadingUserId] = useState(null);
  const [compoundFilter, setCompoundFilter] = useState("");
  const [compoundList, setCompoundList] = useState([]);

  const roles = ["TENANT", "LANDLORD", "SERVICE_PROVIDER"];
  const verifyStates = ["Verified", "Unverified"];
  const userStatuses = ["active", "inactive", "pending"];

  const [roleFilters, setRoleFilters] = useState([]);
  const [verifyFilters, setVerifyFilters] = useState([]);
  const [statusFilters, setStatusFilters] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchChart();
    fetchUsers();
    fetchCompounds();
  }, []);

  const fetchCompounds = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/getCompoundList`, {
        withCredentials: true,
      });
      setCompoundList(res.data.compounds); // expects array of compound names
    } catch (error) {
      console.error("Failed to load compounds", error);
    }
  };
  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/admin/userStats`, {
        withCredentials: true,
      });
      setStats(res.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChart = async () => {
    try {
      setLoading(true);

      const res = await axios.get(`${BACKEND_URL}/api/admin/userChartData`, {
        withCredentials: true,
      });
      setChartData(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFlag = async (userId, userRole) => {
    try {
      setLoadingUserId(userId);
      const res = await axios.post(`${BACKEND_URL}/api/admin/setFlagUser`, {
        id: userId,
        role: userRole,
      });
      const updatedUsers = users.map((u) =>
        u.id === userId ? { ...u, isFlagged: !u.isFlagged } : u
      );

      setUsers(updatedUsers);

      // ✅ Recompute flaggedUsers from updatedUsers
      setFlaggedUsers(updatedUsers.filter((u) => u.isFlagged === true));
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingUserId(null);
    }
  };
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/admin/getUsers`, {
        withCredentials: true,
      });
      setUsers(res.data);
      setFlaggedUsers(res.data.filter((u) => u.isFlagged === true));
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter((u) => {
    const matchesSearch =
      u.username.includes(search) ||
      u.email.includes(search) ||
      u.phone.includes(search);

    const matchesRole =
      roleFilters.length === 0 || roleFilters.includes(u.role);

    const matchesVerify =
      verifyFilters.length === 0 ||
      verifyFilters.includes(u.verified ? "Verified" : "Unverified");

    const matchesStatus =
      statusFilters.length === 0 || statusFilters.includes(u.status);

    const matchesCompound =
      !compoundFilter || u.compoundName === compoundFilter;

    return (
      matchesSearch &&
      matchesRole &&
      matchesVerify &&
      matchesStatus &&
      matchesCompound
    );
  });

  const paginatedUsers = filteredUsers.slice(
    (page - 1) * pageSize,
    page * pageSize
  );
  return (
    <Box>
      <BreadcrumbsBar />
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="80vh"
        >
          <CircularProgress color="success" />
        </Box>
      ) : (
        <Box display="flex" justifyContent="space-around" mt={3}>
          <Grid
            container
            spacing={1}
            columns={{ xs: 2, sm: 8, md: 10 }}
            sx={{
              width: "100%",
            }}
          >
            <Grid item size={{ xs: 2, sm: 3, md: 3 }}>
              <Box
                sx={{
                  p: 1,
                  height: "80vh",
                  display: "flex",
                  flexDirection: "column",
                }}
                elevation={3}
                justifyContent="space-evenly"
              >
                <Grid container columns={{ xs: 2, sm: 4, md: 10 }} spacing={1}>
                  {Object.entries(stats.summary || {}).map(
                    ([label, value], i) => (
                      <Grid item size={{ xs: 1, sm: 4, md: 5 }}>
                        <StatsCard
                          label={label}
                          value={value}
                          icon={iconMap[label]}
                        />
                      </Grid>
                    )
                  )}

                  <Grid item size={{ xs: 2, sm: 8, md: 10 }}>
                    <Paper
                      sx={{
                        padding: 2,
                        height: "20vh",
                      }}
                      elevation={1}
                    >
                      <Typography variant="h6" gutterBottom>
                        User Status (Last 10 Days)
                      </Typography>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <Tooltip />
                          <Bar
                            dataKey="total"
                            fill="#8884d8"
                            name="Total Users"
                          />
                          <Bar
                            dataKey="active"
                            fill="#82ca9d"
                            name="Active Users"
                          />
                          <Bar dataKey="new" fill="#ffc658" name="New Users" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                  <Grid item size={{ xs: 2, sm: 8, md: 10 }}>
                    <Paper
                      sx={{
                        padding: 2,
                        height: "30vh",
                        flexDirection: "column",
                        display: "flex",
                      }}
                      elevation={1}
                    >
                      <Typography variant="h6" gutterBottom>
                        Flagged Users
                      </Typography>
                      <Paper sx={{ overflowX: "auto" }}>
                        <Stack spacing={2}>
                          {flaggedUsers.map((user) => (
                            <UserCard user={user} single />
                          ))}
                        </Stack>
                      </Paper>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
            <Grid item size={{ xs: 2, sm: 5, md: 7 }}>
              <Paper
                sx={{
                  padding: 2,
                  height: "80vh",
                }}
                elevation={0}
              >
                <Grid container spacing={1} columns={{ xs: 2, sm: 4, md: 10 }}>
                  <Grid item size={{ xs: 2, sm: 4, md: 7 }}>
                    <Box
                      md={2}
                      sx={{
                        height: "70vh",
                        overflowY: "auto",
                        pr: 1,
                        scrollbarWidth: "thin",
                        "&::-webkit-scrollbar": {
                          width: "8px",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#f0f0f0",
                          borderRadius: "8px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#c1c1c1",
                          borderRadius: "8px",
                          border: "2px solid #f0f0f0",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                          backgroundColor: "#a0a0a0",
                        },
                      }}
                    >
                      <Stack spacing={2}>
                        {paginatedUsers.length == 0 ? (
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            py={5}
                          >
                            <Warning
                              sx={{ fontSize: 64, mb: 2 }}
                              color="warning"
                            />
                            <Typography
                              variant="h6"
                              color="textSecondary"
                              textAlign="center"
                            >
                              Not found Users
                            </Typography>
                          </Box>
                        ) : (
                          paginatedUsers.map((user) => (
                            <UserCard
                              key={user.id}
                              user={user}
                              showDetails={true}
                              handleFlag={() => handleFlag(user.id, user.role)}
                              loadingUserId={loadingUserId}
                            />
                          ))
                        )}
                      </Stack>
                    </Box>
                    <Box mt={3} position="relative">
                      <Pagination
                        count={Math.ceil(filteredUsers.length / pageSize)}
                        page={page}
                        onChange={(e, value) => setPage(value)}
                        sx={{ justifyContent: "center", display: "flex" }}
                      />
                      <FormControl
                        size="small"
                        sx={{
                          position: "absolute",
                          right: 0,
                          top: "50%",
                          transform: "translateY(-50%)",
                          minWidth: 100,
                        }}
                      >
                        <Select
                          value={pageSize}
                          onChange={(e) => {
                            setPage(1);
                            setPageSize(parseInt(e.target.value));
                          }}
                        >
                          {[5, 10, 15, 20].map((size) => (
                            <MenuItem key={size} value={size}>
                              {size}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>
                  <Grid item size={{ xs: 2, sm: 4, md: 3 }}>
                    <Paper
                      sx={{ p: 2, position: "sticky", top: 80, height: "100%" }}
                      elevation={1}
                    >
                      <TextField
                        placeholder="Search by name, email, phone"
                        fullWidth
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton>
                                <SearchIcon />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      {renderFilterGroup(
                        "Role",
                        roles,
                        roleFilters,
                        setRoleFilters
                      )}
                      {renderFilterGroup(
                        "Verification",
                        verifyStates,
                        verifyFilters,
                        setVerifyFilters
                      )}
                      {renderFilterGroup(
                        "User Status",
                        userStatuses,
                        statusFilters,
                        setStatusFilters
                      )}

                      <Select
                        fullWidth
                        displayEmpty
                        value={compoundFilter}
                        onChange={(e) => setCompoundFilter(e.target.value)}
                        sx={{ mt: 2 }}
                        className="notranslate"
                      >
                        <MenuItem value="" className="notranslate">All Compounds</MenuItem>
                        {compoundList.map((compound) => (
                          <MenuItem
                            key={compound.id}
                            value={compound.name}
                            className="notranslate"
                          >
                            {compound.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <Button
                        fullWidth
                        variant="outlined"
                        sx={{ mt: 2 }}
                        onClick={() => {
                          setFilter("");
                          setVerifyFilters("");
                          setStatusFilters("");
                          setCompoundFilter("");
                        }}
                      >
                        Clear Filters
                      </Button>
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default UserPage;
